'use client';

import type { PricingList } from '@micro-apps/stores';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../../bev-safe-text';

type Props = {
	pricingList: PricingList;
	pricingListDisclaimer?: string;
};

export function ExperiencePricesModalBody({ pricingList, pricingListDisclaimer }: Props) {
	const t = useTranslations();

	return (
		// Figma "title, detail" itemSpacing is bound to stack-m. "policy modal"
		// paddingBottom is bound to card-padding-l on mobile (40) and
		// card-padding-m on desktop (50).
		<div className="flex w-full flex-col gap-y-stack-m">
			<h2 className={cn('text-subtitle1')}>{t('Price List')}</h2>

			{pricingListDisclaimer && (
				<p className="text-body3 text-text-secondary">
					<BevSafeText>{pricingListDisclaimer}</BevSafeText>
				</p>
			)}

			<div className="flex flex-col gap-y-stack-m pb-card-padding-l md:pb-card-padding-m">
				<div className="border-t border-border-subtle" />

				{pricingList.map((option, index) => (
					<div key={option.label ?? index}>
						<ExperiencePriceOption {...option} />

						{index < pricingList.length - 1 && (
							<div className="mt-stack-m w-full border-t border-border-subtle" />
						)}
					</div>
				))}
			</div>
		</div>
	);
}

function ExperiencePriceOption({ label, secondaryLabel, prices }: PricingList[number]) {
	return (
		<div className="flex flex-col gap-y-stack-s">
			{(label || secondaryLabel) && (
				<div className="flex flex-col gap-y-stack-xs">
					{label && (
						<h3 className="text-subtitle1 text-text-heading">
							<BevSafeText>{label}</BevSafeText>
						</h3>
					)}

					{secondaryLabel && (
						<p className="text-overline">
							<BevSafeText>{secondaryLabel}</BevSafeText>
						</p>
					)}
				</div>
			)}

			<dl className="flex flex-col gap-y-stack-s">
				{(prices ?? []).map((price, index) => (
					<div key={price.label ?? index} className="flex justify-between gap-100">
						<dt className="text-body2">
							<BevSafeText>{price.label}</BevSafeText>
						</dt>
						<dd className="text-subtitle2">{price.amount}</dd>
					</div>
				))}
			</dl>
		</div>
	);
}
